/**
 * SVG Service - Centralized service for SVG operations and DOM queries
 * Provides abstraction layer for SVG-related operations to improve testability
 * and separation of concerns.
 */

import { RENDERING } from "../config/constants";

export interface SvgElement {
	element: SVGSVGElement;
	getBoundingClientRect(): DOMRect;
	querySelector<T extends Element>(selector: string): T | null;
}

export interface SvgExportOptions {
	width?: number;
	height?: number;
	backgroundColor?: string;
}

class SvgServiceImpl {
	private static instance: SvgServiceImpl;

	public static getInstance(): SvgServiceImpl {
		if (!SvgServiceImpl.instance) {
			SvgServiceImpl.instance = new SvgServiceImpl();
		}
		return SvgServiceImpl.instance;
	}

	/**
	 * Find the first SVG element in the document
	 */
	public findSvgElement(): SVGSVGElement | null {
		return document.querySelector("svg");
	}

	/**
	 * Find all SVG elements in the document
	 */
	public findAllSvgElements(): NodeListOf<SVGSVGElement> {
		return document.querySelectorAll("svg");
	}

	/**
	 * Count the number of SVG elements in the document
	 */
	public countSvgElements(): number {
		return document.querySelectorAll("svg").length;
	}

	/**
	 * Export SVG element as string with optional styling
	 */
	public exportSvgAsString(
		svgElement: SVGSVGElement,
		options: SvgExportOptions = {},
	): string {
		const clone = svgElement.cloneNode(true) as SVGSVGElement;

		// Apply export options
		if (options.width) {
			clone.setAttribute("width", options.width.toString());
		}
		if (options.height) {
			clone.setAttribute("height", options.height.toString());
		}
		if (options.backgroundColor) {
			clone.style.backgroundColor = options.backgroundColor;
		}

		// Ensure proper XML namespace
		clone.setAttribute("xmlns", RENDERING.SVG_NAMESPACE);
		clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");

		return new XMLSerializer().serializeToString(clone);
	}

	/**
	 * Create a download link for SVG content
	 */
	public downloadSvg(
		svgContent: string,
		filename: string = "triangulation.svg",
	): void {
		const blob = new Blob([svgContent], { type: "image/svg+xml" });
		const url = URL.createObjectURL(blob);

		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		// Clean up the object URL
		URL.revokeObjectURL(url);
	}

	/**
	 * Get SVG element dimensions
	 */
	public getSvgDimensions(svgElement: SVGSVGElement): {
		width: number;
		height: number;
	} {
		const rect = svgElement.getBoundingClientRect();
		return {
			width: rect.width,
			height: rect.height,
		};
	}

	/**
	 * Clear all child elements from an SVG
	 */
	public clearSvg(svgElement: SVGSVGElement): void {
		while (svgElement.firstChild) {
			svgElement.removeChild(svgElement.firstChild);
		}
	}

	/**
	 * Create SVG element with proper namespace
	 */
	public createSvgElement<K extends keyof SVGElementTagNameMap>(
		tagName: K,
	): SVGElementTagNameMap[K] {
		return document.createElementNS(RENDERING.SVG_NAMESPACE, tagName);
	}

	/**
	 * Batch append children using DocumentFragment for performance
	 */
	public batchAppendChildren(
		parent: SVGElement,
		children: SVGElement[],
	): void {
		const fragment = document.createDocumentFragment();
		children.forEach((child) => fragment.appendChild(child));
		parent.appendChild(fragment);
	}
}

export const SvgService = SvgServiceImpl.getInstance();
